import { AnyAction, Dispatch } from "redux";
import firebase, { DocumentData } from "store/firebase";
import "firebase/firestore";
import { StoreError } from "store/errors";
import { RootState } from "store/reducer";
import { firestoreCount } from "store/util/firestore-count";
import { loadIdResource } from "store/util/id-resource";
import { answersActions } from "./reducer";
import { answerFromDoc, newAnswersData } from "./utils";
import { subscribe } from "./listen-change";

// Re-exported from the id-resource action types so existing imports keep
// working — the action TYPE strings differ now (they live under "answers/*"),
// but tests / hooks reference these symbolic constants instead of the strings.
import { ANSWERS_ACTION_TYPES } from "./reducer";
export const ACTION_EVENT_ANSWERS_ITEM_LOADING =
  ANSWERS_ACTION_TYPES.startedLoading;
export const ACTION_EVENT_ANSWERS_LOAD_ITEM = ANSWERS_ACTION_TYPES.loaded;
export const ACTION_EVENT_ANSWERS_LOAD_ITEM_ERROR =
  ANSWERS_ACTION_TYPES.errored;

export default function loadItem(eventId: string) {
  return async (
    dispatch: Dispatch<AnyAction>,
    getState: () => RootState
  ) => {
    const userId = getState().auth?.user?.email;
    return loadIdResource<TastingAnswerEntry, RootState>(
      eventId,
      async (assertActive) => {
        const eventDocRef = firebase
          .firestore()
          .collection("events")
          .doc(eventId);
        const itemRef = eventDocRef.collection("answers").doc(userId ?? "");
        let [doc, event] = await Promise.all([
          itemRef.get(),
          eventDocRef.get(),
        ]);
        assertActive();
        if (!doc.exists) {
          const rawRounds = event?.data()?.rounds;
          const rounds = firestoreCount(rawRounds);
          if (rawRounds != null && rounds == null) {
            throw new StoreError("This event has an invalid round count.");
          }
          const blank = newAnswersData(rounds ?? 0);
          try {
            await itemRef.set(blank as DocumentData);
            doc.data = () => blank;
          } catch (error) {
            if ((error as { code?: string })?.code !== "permission-denied") throw error;
            // Another tab may have initialized this answer after our absent read.
            const existing = await itemRef.get({ source: "server" });
            if (!existing.exists) throw error;
            doc = existing;
          }
        }
        assertActive();
        if (userId) {
          subscribe(eventId, userId);
        }
        return answerFromDoc(doc, eventId);
      },
      {
        actions: answersActions,
        selectSlice: (root) => root.answers,
        requestScope: userId,
      }
    )(dispatch as any, getState);
  };
}

// Local alias to avoid a circular import (reducer.ts imports from listen-change.ts
// which imports nothing from this file; load-item only references the type).
type TastingAnswerEntry = ReturnType<typeof answerFromDoc>;
